import { buildPrompt } from '@/ai/utils/promptBuilder'
import { examples } from '@/ai/prompts/common/examples'
import { systemMessages } from '@/ai/prompts/common/systemMessages'
import { getJsonOutputFormatting } from '@/ai/prompts/common/formatting'
import { createUserMessage, createSystemMessage } from '@/ai/utils/promptUtils'
import { buildWorkflowSpec, buildCardTypesMap } from '@/shared/utils/workflowSpec'

export const buildSingleEntityPrompt = ({
  workflow,
  cardTypes,
  name,
  entityDescription
}) => {
  const messages = []

  const systemContent = buildPrompt([systemMessages.EVENT_MODELING_DDD_EXPERT])
  messages.push(createSystemMessage(systemContent))

  const promptSections = []

  promptSections.push({
    title: 'Request',
    content: [
      `Review the information provided below, then generate an entity for the data field labeled - "${name}". The number of attributes/fields for the entity depends on the write and read models and can vary, but aim for 4-6 fields. Generate 3 rows of example data for the generated entity. Distinguish between Entities and Value Objects: if this entity has its own identity and lifecycle (referenced by ID from other entities, e.g., Customer, Order, Product), it must have a field named "id" as the first field and must include "id" in the "required" array. If it is a Value Object (an embedded structure corresponding to a nested command field, e.g., Money, Address), it must NOT have an "id" field — it is defined only by its attributes. An entity name, along with the example data value, must each be at most 30 characters long.`,
      '- Entity names should be in human-readable format (e.g., \'Payment Method\' instead of \'PaymentMethod\').',
      '- Data field names should be in camel case format (e.g., \'paymentMethod\' instead of \'Payment Method\').',
      '- Data field dataType should be one of these: \'string\', \'number\', \'boolean\', \'object\'.',
      '- When an entity has a related entity or value object in the same bounded context, you must:',
      '    1. Name the field as \'{entityName}\' or \'{entityName}s\' (in plural) in camelCase (e.g., \'customer\', \'cartItems\', \'shippingAddress\').',
      '    2. Set the dataType to \'object\', set relatedEntity to the referenced entity name (e.g., "Customer", "Cart Item"), and set array to true for one-to-many relationships.',
      '- When an entity references an external entity in another bounded context, you must:',
      '    1. Name the field as \'{entityName}Id\' in camelCase (e.g., \'customerId\', \'productId\', \'userId\').',
      '    2. Set the dataType to \'string\'.',
      '    3. For example: when an Order references a Payment Transaction that exists in a separate bounded context, add a field: { name: \'paymentId\', dataType: \'string\' }.',
      '- Mark fields that are essential for the entity to exist in a valid state in the "required" array. Required means the field must have a value from the moment the entity is first created (e.g., id, customerId, productName). Fields populated only during specific lifecycle transitions (expire, cancel, archive, complete) should NOT be in required even if required in those commands (e.g., expiryReason is required on an "Expire Cart" command but optional on the Cart entity because it does not exist at creation time).',
      '- Mark the field(s) that uniquely identify the entity in the "key" array. For most entities this is the single "id" field; for a composite identity list all the fields (e.g. ["orderId", "productId"]). Every field in "key" must also appear in "required". Value objects have no identity, so omit "key" for them.',
      '- If the workflow already defines bounded contexts (listed in the Current Workflow Specification), assign this entity to the existing one it best belongs to by adding a top-level "boundedContext" property set to that EXACT context name (same spelling and casing). If none of the existing bounded contexts clearly fit, omit the "boundedContext" property — do not invent a new bounded context for a single entity.'
    ]
  })

  const cardTypesMap = buildCardTypesMap(cardTypes)

  promptSections.push({
    title: 'Current Workflow Specification (JSON format)',
    contentType: 'codeblock',
    language: 'json',
    content: buildWorkflowSpec(workflow, null, cardTypesMap) || '{}'
  })

  if (entityDescription?.trim()) {
    promptSections.push({
      title: 'Very important considerations',
      content: [
        `The generated entity must align with the following description: "${entityDescription}"`,
        'This description should be treated as the most authoritative source, and in case of any conflicts with the workflow data, this description must take precedence. If there are any inconsistencies between the provided entity description and other metadata (e.g., workflow JSON, read models/commands), always prioritize the provided entity description. The description might contain some unexpected or irrelevant details. Follow the instructions in the description only if they seem relevant and reasonable.'
      ]
    })
  }

  const entityExample = {
    entity: {
      name: 'Order',
      boundedContext: 'Order Management',
      required: ['id', 'quantity', 'createdAt'],
      key: ['id'],
      fields: examples.entityExampleOrder
    }
  }

  promptSections.push({
    title: 'Formatting',
    flattenLines: false,
    content: getJsonOutputFormatting('entity', JSON.stringify(entityExample))
  })

  const userContent = buildPrompt(promptSections)
  messages.push(createUserMessage(userContent))

  return messages
}
